import { CommandItem } from "@/components/ui/command";
import {
  DropdownMenuItem,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@/components/ui/popover";
import { getObjectActionDefinition } from "@/object-actions/catalog";
import {
  DestinationCommandMenuSub,
  DestinationDropdownMenuSub,
} from "@/object-actions/renderers/DestinationSubmenu";
import type {
  ActionLabelContext,
  ObjectActionId,
  ResolvedObjectAction,
} from "@/object-actions/types";
import { getActionIcon, getActionLabel } from "@/object-actions/utils";
import { cn } from "@/utils";
import { ChevronRight } from "lucide-react";
import { useRef, useState } from "react";

export interface ObjectActionMenuItemProps {
  action: ResolvedObjectAction;
  labelCtx: ActionLabelContext;
  /** Dropdown (default) or command palette row. */
  variant?: "dropdown" | "command";
  iconClassName?: string;
  onSelect?: () => void;
}

function resolveIconClassName(
  action: ResolvedObjectAction,
  iconClassName?: string,
): string {
  const isStar = action.id === "star" || action.id === "unstar";
  return cn(
    iconClassName,
    isStar && action.id === "unstar" && "text-amber-500 fill-amber-500",
  );
}

/**
 * Command-palette row for a multi-variant action (e.g. Download → formats).
 * Renders the parent row with a chevron and opens the variants in a nested,
 * non-portaled popover so clicking a variant doesn't dismiss the outer menu.
 */
function ObjectActionCommandSubmenuItem({
  action,
  label,
  icon,
  onSelect,
}: {
  action: ResolvedObjectAction;
  label: string;
  icon: React.ReactNode;
  onSelect?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<number | undefined>(undefined);

  const openNow = () => {
    window.clearTimeout(closeTimer.current);
    if (!action.disabled) setOpen(true);
  };
  const closeSoon = () => {
    window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setOpen(false), 120);
  };

  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverAnchor asChild>
        <CommandItem
          disabled={action.disabled}
          onMouseEnter={openNow}
          onMouseLeave={closeSoon}
          onSelect={openNow}
        >
          {icon}
          {label}
          <ChevronRight className="ml-auto h-4 w-4 opacity-60" />
        </CommandItem>
      </PopoverAnchor>
      <PopoverContent
        align="start"
        disablePortal
        side="right"
        sideOffset={4}
        className="w-[200px] p-1"
        onMouseEnter={openNow}
        onMouseLeave={closeSoon}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {action.variants?.map((entry) => (
          <CommandItem
            key={entry.id}
            disabled={action.disabled || entry.disabled}
            onSelect={() => {
              onSelect?.();
              setOpen(false);
              window.setTimeout(() => entry.handler(), 0);
            }}
          >
            {entry.label}
          </CommandItem>
        ))}
      </PopoverContent>
    </Popover>
  );
}

export function ObjectActionMenuItem({
  action,
  labelCtx,
  variant = "dropdown",
  iconClassName,
  onSelect,
}: ObjectActionMenuItemProps) {
  const Icon = getActionIcon(action.definition, labelCtx);
  const label = getActionLabel(action.definition, labelCtx);
  const iconCn = resolveIconClassName(action, iconClassName);

  if (variant === "command") {
    if (action.submenu) {
      return (
        <DestinationCommandMenuSub
          submenu={action.submenu}
          label={label}
          icon={<Icon className={iconCn} />}
          disabled={action.disabled}
        />
      );
    }

    if (action.variants && action.variants.length > 1) {
      return (
        <ObjectActionCommandSubmenuItem
          action={action}
          label={label}
          icon={<Icon className={iconCn} />}
          onSelect={onSelect}
        />
      );
    }

    return (
      <CommandItem
        disabled={action.disabled}
        // Destroy-row treatment (matches DropdownMenuItem's `danger`): the
        // design-system destructive red on label AND icon (icons inherit
        // currentColor), kept through the highlighted state.
        className={cn(
          action.definition.destructive &&
            "text-destructive data-[selected=true]:text-destructive data-[selected=true]:bg-destructive/15",
        )}
        onSelect={() => {
          if (!action.disabled) {
            onSelect?.();
            window.setTimeout(() => action.handler(), 0);
          }
        }}
      >
        <Icon className={iconCn} />
        {label}
      </CommandItem>
    );
  }

  if (action.submenu) {
    return (
      <DestinationDropdownMenuSub
        submenu={action.submenu}
        label={label}
        icon={<Icon className={iconCn} />}
        disabled={action.disabled}
      />
    );
  }

  if (action.variants && action.variants.length > 1) {
    return (
      <DropdownMenuSub>
        <DropdownMenuSubTrigger
          disabled={action.disabled}
          onClick={(e) => e.stopPropagation()}
        >
          <Icon className={iconCn} />
          {label}
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent onClick={(e) => e.stopPropagation()}>
          {action.variants.map((entry) => (
            <DropdownMenuItem
              key={entry.id}
              disabled={action.disabled || entry.disabled}
              onClick={(e) => {
                e.stopPropagation();
                window.setTimeout(() => entry.handler(), 0);
              }}
            >
              {entry.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuSubContent>
      </DropdownMenuSub>
    );
  }

  return (
    <DropdownMenuItem
      disabled={action.disabled}
      danger={action.definition.destructive}
      onClick={(e) => {
        e.stopPropagation();
        window.setTimeout(() => action.handler(), 0);
      }}
    >
      <Icon className={iconCn} />
      {label}
      {action.definition.shortcut && (
        <DropdownMenuShortcut>
          {action.definition.shortcut}
        </DropdownMenuShortcut>
      )}
    </DropdownMenuItem>
  );
}

/** Static catalog row when only an ObjectActionId + handler are available (no resolver). */
export function ObjectActionCatalogMenuItem({
  actionId,
  labelCtx,
  onClick,
  disabled,
  iconClassName,
}: {
  actionId: ObjectActionId;
  labelCtx: ActionLabelContext;
  onClick: () => void;
  disabled?: boolean;
  iconClassName?: string;
}) {
  const definition = getObjectActionDefinition(actionId);
  const Icon = getActionIcon(definition, labelCtx);
  const label = getActionLabel(definition, labelCtx);

  return (
    <DropdownMenuItem
      disabled={disabled}
      danger={definition.destructive}
      onClick={(e) => {
        e.stopPropagation();
        window.setTimeout(onClick, 0);
      }}
    >
      <Icon className={iconClassName} />
      {label}
    </DropdownMenuItem>
  );
}
